'use client';

import { c as _c } from "react/compiler-runtime";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { LazyMotion, domAnimation, m } from 'framer-motion';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from '@relume_io/relume-ui';
import { SECTION_NAV_BUTTON_CLASS } from '@/components/ui/SectionNavButton';
import ServiceCardDescription from '@/components/ServiceCardDescription';
import { hasServiceCardDescription, type ServiceCardDescription as ServiceCardDescriptionValue } from '@/lib/service-card-description';
export type ServiceCardVM = {
  _key: string;
  title: string;
  href: string;
  description: ServiceCardDescriptionValue;
  imgSrc: string | null;
  imgBlur: string | null;
};
const SANITY_WIDTHS_FULL = [240, 320, 360, 420, 480, 512, 560, 608, 640, 720, 800, 960] as const;
const SANITY_WIDTHS_LAZY = [320, 480, 640, 800] as const;
function sanityVariant(url: string, w: number, aspectW = 4, aspectH = 5) {
  const u = new URL(url);
  const h = Math.round(aspectH / aspectW * w);
  u.searchParams.set('w', String(w));
  u.searchParams.set('h', String(h));
  if (!u.searchParams.has('auto')) u.searchParams.set('auto', 'format');
  if (!u.searchParams.has('q')) u.searchParams.set('q', '70');
  if (u.searchParams.has('dpr')) u.searchParams.set('dpr', '1');
  return u.toString();
}
const ResponsiveSanityImg = memo(function ResponsiveSanityImg(t0) {
  const $ = _c(16);
  const {
    src,
    alt,
    sizes,
    priority: t1
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const widths = priority ? SANITY_WIDTHS_FULL : SANITY_WIDTHS_LAZY;
  let t2;
  if ($[0] !== src || $[1] !== widths) {
    let t3;
    if ($[3] !== src) {
      t3 = w => `${sanityVariant(src, w)} ${w}w`;
      $[3] = src;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = widths.map(t3).join(", ");
    $[0] = src;
    $[1] = widths;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const srcSet = t2;
  let t3;
  if ($[5] !== src || $[6] !== widths[0]) {
    t3 = sanityVariant(src, widths[0]);
    $[5] = src;
    $[6] = widths[0];
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const finalSrc = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = clsx("absolute inset-0 h-full w-full object-cover", "transform-gpu backface-hidden", "[@media(hover:hover)]:transition-transform [@media(hover:hover)]:duration-700", "[@media(hover:hover)]:group-hover:scale-110");
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = priority ? "eager" : "lazy";
  const t6 = priority ? "high" : "auto";
  let t7;
  if ($[9] !== alt || $[10] !== finalSrc || $[11] !== sizes || $[12] !== srcSet || $[13] !== t5 || $[14] !== t6) {
    t7 = <div className="absolute inset-0 bg-gray-50/10"><img src={finalSrc} srcSet={srcSet} sizes={sizes} alt={alt} className={t4} loading={t5} decoding="async" fetchPriority={t6} /></div>;
    $[9] = alt;
    $[10] = finalSrc;
    $[11] = sizes;
    $[12] = srcSet;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
});
function useFinePointer(t0) {
  const $ = _c(3);
  const enabled = t0 === undefined ? true : t0;
  const [hasFinePointer, setHasFinePointer] = useState(false);
  let t1;
  let t2;
  if ($[0] !== enabled) {
    t1 = () => {
      if (!enabled) {
        setHasFinePointer(false);
        return;
      }
      const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
      const sync = () => setHasFinePointer(mq.matches);
      sync();
      mq.addEventListener("change", sync);
      return () => mq.removeEventListener("change", sync);
    };
    t2 = [enabled];
    $[0] = enabled;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return hasFinePointer;
}
type CarouselTheme = 'light' | 'dark';
const CardDescriptionsPanel = memo(function CardDescriptionsPanel(t0) {
  const $ = _c(17);
  const {
    cards,
    activeKey,
    idPrefix: t1,
    className,
    theme: t2
  } = t0;
  const idPrefix = t1 === undefined ? "service-card" : t1;
  const theme = t2 === undefined ? "light" : t2;
  const t3 = theme === "dark" ? "text-primary" : "text-black";
  let t4;
  if ($[0] !== t3) {
    t4 = clsx("text-base leading-relaxed", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const descClass = t4;
  let t5;
  if ($[2] !== activeKey || $[3] !== cards) {
    const hoveredCard = activeKey ? cards.find(c => c._key === activeKey) : null;
    t5 = hoveredCard && hasServiceCardDescription(hoveredCard.description) ? hoveredCard.description : null;
    $[2] = activeKey;
    $[3] = cards;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const hoveredDescription = t5;
  const contentKey = hoveredDescription ? activeKey : "empty";
  let t6;
  if ($[5] !== className) {
    t6 = clsx("relative hidden min-h-[5.5rem] min-w-0 max-w-[38ch] md:block md:min-h-[5rem] md:max-w-[32rem]", className);
    $[5] = className;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = contentKey === "empty" ? undefined : `${idPrefix}-desc-${contentKey}`;
  let t8;
  if ($[7] !== descClass || $[8] !== hoveredDescription) {
    t8 = hoveredDescription ? <div className={descClass}><ServiceCardDescription value={hoveredDescription} /></div> : null;
    $[7] = descClass;
    $[8] = hoveredDescription;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== contentKey || $[11] !== t7 || $[12] !== t8) {
    t9 = <div key={contentKey} id={t7}>{t8}</div>;
    $[10] = contentKey;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t6 || $[15] !== t9) {
    t10 = <div className={t6}>{t9}</div>;
    $[14] = t6;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
});
const ServiceCard = memo(function ServiceCard(t0) {
  const $ = _c(35);
  const {
    title,
    href,
    imgSrc,
    priority: t1,
    cardSize: t2,
    animateReveal: t3,
    equalHeight: t4,
    fillMobile: t5,
    onHover,
    onLeave,
    hoverEnabled: t6
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const cardSize = t2 === undefined ? "default" : t2;
  const animateReveal = t3 === undefined ? false : t3;
  const equalHeight = t4 === undefined ? true : t4;
  const fillMobile = t5 === undefined ? false : t5;
  const hoverEnabled = t6 === undefined ? true : t6;
  const sizes = cardSize === "compact" ? "(max-width: 767px) calc(72vw - 16px), 260px" : cardSize === "xlarge" ? "(max-width: 767px) calc(82vw - 16px), (max-width: 1279px) 380px, 420px" : cardSize === "large" ? "(max-width: 767px) calc(78vw - 16px), (max-width: 1279px) 340px, 360px" : "(max-width: 479px) calc(74vw - 16px), (max-width: 639px) calc(64vw - 16px), (max-width: 767px) calc(52vw - 16px), 304px";
  const widthClass = fillMobile ? clsx("w-full max-w-none", cardSize === "large" ? "md:w-[340px] lg:w-[360px]" : cardSize === "compact" ? "md:w-[240px]" : "md:w-[320px]") : cardSize === "compact" ? "w-[72vw] max-w-[260px] sm:w-[240px]" : cardSize === "xlarge" ? "w-[82vw] max-w-[380px] sm:w-[340px] md:w-[380px] lg:w-[420px]" : cardSize === "large" ? "w-[78vw] max-w-[340px] sm:w-[300px] md:w-[340px] lg:w-[360px]" : "w-[74vw] xs:w-[64vw] sm:w-[52vw] md:w-[320px]";
  let t7;
  if ($[0] !== imgSrc || $[1] !== priority || $[2] !== sizes || $[3] !== title) {
    t7 = imgSrc ? <ResponsiveSanityImg src={imgSrc} alt={title || ""} sizes={sizes} priority={priority} /> : null;
    $[0] = imgSrc;
    $[1] = priority;
    $[2] = sizes;
    $[3] = title;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const image = t7;
  const startRef = useRef(null);
  const isDraggingRef = useRef(false);
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e => {
      const touch = e.touches[0];
      startRef.current = {
        x: touch.clientX,
        y: touch.clientY
      };
      isDraggingRef.current = false;
    };
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const handleTouchStart = t8;
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = e_0 => {
      if (!startRef.current) {
        return;
      }
      const touch_0 = e_0.touches[0];
      const dx = Math.abs(touch_0.clientX - startRef.current.x);
      const dy = Math.abs(touch_0.clientY - startRef.current.y);
      if (dx > 6 || dy > 6) {
        isDraggingRef.current = true;
      }
    };
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const handleTouchMove = t9;
  let t10;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_1 => {
      if (isDraggingRef.current) {
        e_1.preventDefault();
        e_1.stopPropagation();
        isDraggingRef.current = false;
      }
    };
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  const handleClickCapture = t10;
  const t11 = clsx("group relative flex flex-col overflow-hidden rounded-[40px] bg-primary-light pb-4 text-black", equalHeight && "h-full", widthClass, "[@media(hover:hover)]:transition-colors [@media(hover:hover)]:duration-700 [@media(hover:hover)]:ease-in-out", "[@media(hover:hover)]:hover:bg-accent [@media(hover:hover)]:hover:text-primary-light");
  const t12 = hoverEnabled ? onHover : undefined;
  const t13 = hoverEnabled ? onLeave : undefined;
  const t14 = href || "#";
  const t15 = !hoverEnabled && "touch-pan-y";
  let t16;
  if ($[8] !== t15) {
    t16 = clsx("absolute inset-0 z-10", "[-webkit-tap-highlight-color:transparent]", t15);
    $[8] = t15;
    $[9] = t16;
  } else {
    t16 = $[9];
  }
  const t17 = title || "Przejd\u017A do szczeg\xF3\u0142\xF3w";
  let t18;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      userSelect: "none"
    };
    $[10] = t18;
  } else {
    t18 = $[10];
  }
  let t19;
  if ($[11] !== handleClickCapture || $[12] !== handleTouchMove || $[13] !== handleTouchStart || $[14] !== t14 || $[15] !== t16 || $[16] !== t17) {
    t19 = <Link href={t14} scroll={true} className={t16} aria-label={t17} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onClickCapture={handleClickCapture} style={t18} />;
    $[11] = handleClickCapture;
    $[12] = handleTouchMove;
    $[13] = handleTouchStart;
    $[14] = t14;
    $[15] = t16;
    $[16] = t17;
    $[17] = t19;
  } else {
    t19 = $[17];
  }
  let t20;
  if ($[18] !== animateReveal || $[19] !== image) {
    t20 = <div className="pointer-events-none block p-2"><div className="relative aspect-[4/5] w-full overflow-hidden rounded-[32px] bg-primary-light ios-clip-fix">{animateReveal ? <><div className="relative h-full w-full [@media(hover:hover)_and_(pointer:fine)]:hidden">{image}</div><div className="absolute inset-0 hidden overflow-hidden ios-clip-fix [@media(hover:hover)_and_(pointer:fine)]:block"><m.div initial={{
              y: "-100%"
            }} animate={{
              y: 0
            }} transition={{
              duration: 1.1,
              ease: [0.45, 0, 0.55, 1],
              delay: 0.2
            }} className="absolute inset-0 overflow-hidden ios-clip-fix"><m.div initial={{
                y: "100%",
                scale: 1.1
              }} animate={{
                y: 0,
                scale: 1
              }} transition={{
                duration: 1.1,
                ease: [0.45, 0, 0.55, 1],
                delay: 0.2
              }} className="relative h-full w-full">{image}</m.div></m.div></div></> : <div className="relative h-full w-full">{image}</div>}</div></div>;
    $[18] = animateReveal;
    $[19] = image;
    $[20] = t20;
  } else {
    t20 = $[20];
  }
  const t21 = equalHeight && "flex flex-1 flex-col justify-start";
  let t22;
  if ($[21] !== t21) {
    t22 = clsx("pointer-events-none relative z-20 px-5 pt-2 md:px-6", t21);
    $[21] = t21;
    $[22] = t22;
  } else {
    t22 = $[22];
  }
  let t23;
  if ($[23] !== title) {
    t23 = title ? <h3 className="text-right text-sm font-medium uppercase leading-snug tracking-[0.1em] md:text-base">{title}</h3> : null;
    $[23] = title;
    $[24] = t23;
  } else {
    t23 = $[24];
  }
  let t24;
  if ($[25] !== t22 || $[26] !== t23) {
    t24 = <div className={t22}>{t23}</div>;
    $[25] = t22;
    $[26] = t23;
    $[27] = t24;
  } else {
    t24 = $[27];
  }
  let t25;
  if ($[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t19 || $[32] !== t20 || $[33] !== t24) {
    t25 = <article className={t11} onMouseEnter={t12} onMouseLeave={t13}>{t19}{t20}{t24}</article>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t19;
    $[32] = t20;
    $[33] = t24;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  return t25;
});

/** Do prawej krawędzi viewportu — ten sam wzór co galeria (THUMB_BLEED). */
const RIGHT_BLEED = 'relative overflow-hidden mr-[calc(50%-50vw)] w-[calc(100%+50vw-50%)] max-w-[100vw]';
export type ServiceCategoryCarouselProps = {
  cards: ServiceCardVM[];
  categoryDescription: string;
  variant?: 'bleed' | 'contained' | 'right-bleed';
  theme?: CarouselTheme;
  idPrefix?: string;
  className?: string;
  /** Gdy false — bez panelu opisu pod karuzelą przy hoverze. */
  showHoverDescription?: boolean;
  /** Nadpisuje domyślny rozmiar wynikający z variant. */
  cardSize?: 'default' | 'compact' | 'large' | 'xlarge';
  /** Zapętlenie Embla (domyślnie wyłączone). */
  loop?: boolean;
  /** Ile pierwszych obrazów z `priority` / eager (domyślnie 2). */
  priorityCount?: number;
  /** Wyrównanie wysokości kart do najwyższej (domyślnie true). */
  equalHeight?: boolean;
  /**
   * Na mobile karty wypełniają tor (% zamiast vw) — bez ucinania w wąskim boxie.
   */
  fillMobile?: boolean;
  /** Extra klasy na torze / content Embla (np. padding startu). */
  contentClassName?: string;
  /** Extra klasy na wierszu ze strzałkami. */
  controlsClassName?: string;
};
export default memo(function ServiceCategoryCarousel({
  cards,
  variant = 'bleed',
  theme = 'light',
  idPrefix = 'service-card',
  className,
  showHoverDescription = true,
  cardSize: cardSizeProp,
  loop = false,
  priorityCount = 2,
  equalHeight = true,
  fillMobile = false,
  contentClassName,
  controlsClassName
}: ServiceCategoryCarouselProps) {
  const [activeCardKey, setActiveCardKey] = useState<string | null>(null);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const revealPlayedRef = useRef(false);
  const needsCardMotion = showHoverDescription;
  const hasFinePointer = useFinePointer(needsCardMotion);
  const cardSize: 'default' | 'compact' | 'large' | 'xlarge' = cardSizeProp ?? (variant === 'contained' ? 'compact' : variant === 'right-bleed' ? 'large' : 'default');
  const cardsKey = useMemo(() => cards.map(c => c._key).join('|'), [cards]);
  useEffect(() => {
    if (!needsCardMotion) return;
    revealPlayedRef.current = true;
  }, [needsCardMotion]);
  useEffect(() => {
    carouselApi?.scrollTo(0);
    setActiveCardKey(null);
  }, [cardsKey, carouselApi]);
  useEffect(() => {
    if (!hasFinePointer) setActiveCardKey(null);
  }, [hasFinePointer]);
  const onCardHover = useCallback((key: string) => {
    setActiveCardKey(key);
  }, []);
  const onCardLeave = useCallback(() => {
    setActiveCardKey(null);
  }, []);
  if (cards.length === 0) return null;
  const trackClass = variant === 'bleed' ? 'relative -ml-6 overflow-hidden pl-6 md:-ml-7 md:pl-7 w-[calc(100%+(100vw-100%)/2)] max-w-[100vw]' : variant === 'right-bleed' ? RIGHT_BLEED : 'relative w-full overflow-x-clip overflow-y-visible';
  const contentClass = variant === 'right-bleed' ? '-ml-2 pr-[max(1rem,5vw)] sm:pr-[4vw] md:pr-[3vw]' : variant === 'bleed' ? '-ml-2 pr-6' : 'ml-0';
  const carousel = <Carousel setApi={setCarouselApi} opts={{
    loop,
    align: 'start',
    containScroll: false,
    dragFree: false
  }} className={clsx('w-full touch-pan-y overscroll-x-contain', className)}>
            <div className={trackClass}>
                <CarouselContent className={clsx(contentClass, contentClassName)}>
                    {cards.map((c_0, i) => <CarouselItem key={c_0._key} className={clsx('pl-2', fillMobile ? 'basis-[86%] md:basis-auto' : 'basis-auto', equalHeight && 'flex')}>
                            <ServiceCard {...c_0} cardSize={cardSize} equalHeight={equalHeight} fillMobile={fillMobile} priority={i < priorityCount} animateReveal={needsCardMotion && !revealPlayedRef.current && i < 2} hoverEnabled={hasFinePointer && needsCardMotion} onHover={needsCardMotion ? () => onCardHover(c_0._key) : undefined} onLeave={needsCardMotion ? onCardLeave : undefined} />
                        </CarouselItem>)}
                </CarouselContent>
            </div>

            <div className={clsx('relative z-10 mt-6 flex flex-wrap items-end gap-x-6 gap-y-4', needsCardMotion ? 'justify-end md:justify-between' : 'justify-end', controlsClassName)}>
                {needsCardMotion ? <CardDescriptionsPanel cards={cards} activeKey={activeCardKey} idPrefix={idPrefix} theme={theme} /> : null}
                <div className="flex shrink-0 gap-2">
                    <CarouselPrevious className={clsx(SECTION_NAV_BUTTON_CLASS, 'static translate-y-0 [@media(hover:hover)]:transition [@media(hover:hover)]:duration-500')} />
                    <CarouselNext className={clsx(SECTION_NAV_BUTTON_CLASS, 'static translate-y-0 [@media(hover:hover)]:transition [@media(hover:hover)]:duration-500')} />
                </div>
            </div>
        </Carousel>;
  if (!needsCardMotion) return carousel;
  return <LazyMotion features={domAnimation}>{carousel}</LazyMotion>;
});